/* eslint-disable no-mixed-spaces-and-tabs */
import {omit, isBoolean} from 'lodash';
import React from 'react';
import {Link} from 'react-router-dom';
// nodejs library for concatinating classes
import classNames from 'classnames';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';

import IconWrapper from '../wrappers/icon';
// component style
import buttonStyle from '../assets/styles/button/base';

/*
 * Usage example
 *
 * <Button
 *	onClick={this.handleClick} (If link than use props 'to')
 *	color='primary | danger | warning | white | black | transparent | false'
 *	size='xs | sm | lg | xl |  auto | false'
 *	active='bool'
 *	disabled='bool'
 *	border='bool'		(gives button border)
 *	link='bool' 		(gives button style of same as anchor tag)
 *	opacity='bool' 		(gives button opacity color)
 *	simple='bool' 		(button with no background, color applies only on text)
 *	withIcon='bool'		(if button have icon + text)
 *	justIcon='bool'		(if button have only icon | can be fab button too)
 *  fab='bool'			(if button needs to be circle)
 * 	noShadow='bool 		(will remove shadow)
 *	className='string'
 * >
 *	string
 * </Button>
 *
 */

const proxyClickAndLog = (id, handleClick, logEvent) => (...args) => {
	logEvent &&
		logEvent('click_event', {
			data: {
				button: id
			}
		});

	return handleClick(...args);
};

function RegularButton(props) {
	const {
		id,
		testid,
		classes,
		color,
		active,
		upperCase,
		disabled,
		margin,
		size,
		border,
		link,
		opacity,
		simple,
		noShadow,
		fab,
		iconOutlined,
		icon,
		iconSize,
		withIcon,
		justIcon,
		className,
		to,
		handleClick,
		children,
		logEvent,
		fullWidth,
		type,
		sx,
		iconColor = 'inherit',
		...rest
	} = props;

	// console.warn('props', props)

	let filteredProps = rest;

	const btnClasses = classNames({
		[classes.button]: true,
		[classes.disabled]: disabled,
		[classes.active]: active,
		[classes.upperCase]: upperCase,
		[classes[color]]: !disabled && color,
		[classes.marginRatio + margin]: margin,
		[classes[size]]: size,
		[classes.border]: border,
		[classes.link]: link,
		[classes.opacity]: opacity,
		[classes.simple]: simple,
		[classes.noShadow]: noShadow,
		[classes.fab]: fab,
		[classes.withIcon]: withIcon || (icon && children),
		[classes.justIcon]: justIcon || (icon && !children),
		[classes.fullWidth]: fullWidth,
		[className]: className
	});

	// need to remove it if it is boolean, so we dont get prop type errors
	if (isBoolean(filteredProps.onClick)) {
		filteredProps = omit(filteredProps, 'onClick');
	}

	const buttonProps = {
		...filteredProps,
		id,
		type,
		'data-testid': testid || id,
		className: btnClasses,
		classes: {
			label: `${justIcon ? 'material-icons' : 'button-label'}`
		},
		sx
	};

	if (to) {
		buttonProps.component = Link;
		buttonProps.to = to;
	}

	if (handleClick || buttonProps.onClick) {
		if (disabled) {
			buttonProps.onClick = () => null;
		} else {
			buttonProps.onClick = proxyClickAndLog(
				buttonProps.id || buttonProps['data-zegal-component'],
				handleClick || buttonProps.onClick,
				logEvent
			);
		}
	}

	return (
		<Button {...buttonProps}>
			{icon && (
				<IconWrapper size={iconSize && iconSize} outlined={iconOutlined} color={iconColor}>
					{icon}
				</IconWrapper>
			)}

			{children}
		</Button>
	);
}

RegularButton.propTypes = {
	classes: PropTypes.object.isRequired,
	disabled: PropTypes.bool,
	active: PropTypes.bool,
	upperCase: PropTypes.bool,
	color: PropTypes.oneOf([
		'primary',
		'secondary',
		'danger',
		'textSecondary',
		'warning',
		'white',
		'black',
		'transparent',
		false
	]),
	margin: PropTypes.oneOf(['none']),
	size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'auto', false]),
	iconSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'auto', false]),
	border: PropTypes.bool,
	link: PropTypes.bool,
	opacity: PropTypes.bool,
	simple: PropTypes.bool,
	noShadow: PropTypes.bool,
	fab: PropTypes.bool,
	iconOutlined: PropTypes.bool,
	icon: PropTypes.any,
	endIcon: PropTypes.any,
	withIcon: PropTypes.bool,
	justIcon: PropTypes.bool,
	className: PropTypes.string,
	to: PropTypes.any, // could be bool
	handleClick: PropTypes.any, // could be bool
	onClick: PropTypes.any,
	id: PropTypes.string,
	fullWidth: PropTypes.bool,
	disableRipple: PropTypes.bool,
	tabIndex: PropTypes.number,
	type: PropTypes.string,
	style: PropTypes.object,
	iconColor: PropTypes.any,
	startIcon: PropTypes.any,
	sx: PropTypes.any
};

export default withStyles(buttonStyle)(RegularButton);
